import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Mail, RefreshCw } from "lucide-react";

const STATUS_STYLE = {
  approved: "bg-[#27AE60]/15 text-[#1e8449] border-[#27AE60]/40",
  declined: "bg-[#C0392B]/10 text-[#C0392B] border-[#C0392B]/40",
  invited: "bg-amber-100 text-amber-800 border-amber-300",
  "link revoked": "bg-stone-200 text-stone-400 border-stone-300 line-through",
  "not asked": "bg-stone-100 text-stone-500 border-stone-200",
};

export default function WebsitePhotosAdminPage() {
  const [scenes, setScenes] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/website-scenes").then(({ data }) => setScenes(data.scenes || []))
    .catch((e) => { toast.error(formatApiError(e)); setScenes([]); });
  useEffect(() => { load(); }, []);

  const invite = async (sceneIds) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/website-scenes/invite", { scene_ids: sceneIds });
      toast.success(`Invites emailed to ${data.sent.length} people${data.skipped.length ? ` — skipped: ${data.skipped.join(", ")}` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (!scenes) return <div className="p-10 text-center text-stone-400">Loading…</div>;

  return (
    <div className="mx-auto max-w-5xl p-6" data-testid="website-photos-admin">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-stone-900">Website photos — staff consent</h1>
          <p className="mt-1 text-sm text-stone-500">Every photo stays private until each person pictured approves that exact photo. Invites go by email with a private no-password link.</p>
        </div>
        <Button onClick={() => invite([])} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="invite-all-btn">
          <Mail size={14} className="mr-1.5" /> {busy ? "Sending…" : "Email invites to everyone pictured"}
        </Button>
      </div>

      <div className="mt-6 space-y-4">
        {scenes.map((s) => (
          <div key={s.id} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid={`scene-card-${s.id}`}>
            <div className="flex gap-4">
              <img src={s.url} alt={s.title} className="h-24 w-36 rounded-lg object-cover" loading="lazy" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-stone-800">{s.title}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(s.people || []).map((p) => (
                    <span key={p.name} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${STATUS_STYLE[p.status] || STATUS_STYLE["not asked"]}`}
                      title={`${p.email || "no email"}${p.signed_at ? ` · signed ${String(p.signed_at).slice(0, 10)}` : ""}`}
                      data-testid={`scene-person-${s.id}-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>
                      {p.name} — {p.status}
                    </span>
                  ))}
                </div>
                {(s.people || []).some((p) => p.bio) && (
                  <div className="mt-2 space-y-1">
                    {s.people.filter((p) => p.bio).map((p) => (
                      <p key={p.name} className="text-xs italic text-stone-500">"{p.bio}" — {p.name}</p>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => invite([s.id])} disabled={busy}
                className="self-start rounded-full border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-600 hover:bg-stone-50 disabled:opacity-50"
                data-testid={`invite-scene-${s.id}`}>
                <RefreshCw size={11} className="mr-1 inline" /> Invite
              </button>
            </div>
          </div>
        ))}
        {scenes.length === 0 && <p className="text-sm text-stone-500" data-testid="scenes-empty">No website photos registered yet.</p>}
      </div>
    </div>
  );
}
